import {DOCUMENT} from '@angular/common';
import {ChangeDetectorRef, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {HeaderIdentifier} from '@application/headers/header-identifier.enum';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {NavigationNewItemData} from '@application/helper/navigation-helper/navigation-new-item-data.interface';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {BmpDrawing} from '@domain/production-schedule/bmp-drawing';
import {Drawing} from '@domain/production-schedule/drawing';
import {DrawingConfigurationWithColorAndYarnSet} from '@domain/production-schedule/drawing-configuration-with-color-and-yarn-set';
import {DrawingImage} from '@domain/production-schedule/drawing-image';
import {DrawingShapeAbbreviation} from '@domain/production-schedule/drawing-shape-abbreviation';
import {DrawingType} from '@domain/production-schedule/drawing-type.enum';
import {EpDrawing} from '@domain/production-schedule/ep-drawing';
import {TftDrawing} from '@domain/production-schedule/tft-drawing';
import {PropertyValue} from '@domain/property-value';
import {TargetForListOfDrawingsEnum} from '@domain/target-for-list-of-drawings.enum';
import {WeaveQuality} from '@domain/textile-data/machine-quality/weave-quality';
import {DRAWINGS, Drawings} from '@infrastructure/http/drawing/drawings';
import {OverviewListColorSet} from '@presentation/pages/textile-data/color-set/overview/overview-list-color-set';
import {
  AssertionUtils,
  BackendError,
  BaseComponent,
  convertToCommercialUnitMillimeter,
  DialogBuilderFactoryService,
  DialogOptions,
  DialogType,
  FormValidationHelper,
  ObjectUtils,
  ToastService,
  TranslateService,
  Unit
} from '@vdw/angular-component-library';
import {each, filter, find, findLastIndex, first, isEmpty, isEqual, isNil, last, map, remove, size, slice, some} from 'lodash-es';
import {forkJoin} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {AddDrawingFormComponent} from '../../drawing-form/add/add-drawing-form.component';
import {DrawingForm} from '../../drawing-form/drawing-form';
import {NavigationAddDrawingData} from './navigation-add-drawing-data.interface';

@Component({
  selector: 'app-add-drawing',
  templateUrl: './add-drawing.component.html',
  styleUrls: ['./add-drawing.component.scss']
})
export class AddDrawingComponent extends BaseComponent implements OnInit {
  public readonly HEADER_IDENTIFIER = HeaderIdentifier.ADD_DRAWING;

  public drawingImages: DrawingImage[] = [];
  public areImagesUploading = false;
  public currentDrawingFormIndex = 0;
  public drawingShapes: string[] = [];
  public drawingsToBeSaved: Drawing[] = [];
  public defaultDrawingShape: string;

  public duplicateDrawingNames: string[] = [];
  public duplicateDecided = false;
  public overwriteDuplicateNames = false;
  public skipDuplication = false;

  public drawingFormComponent: AddDrawingFormComponent;
  @ViewChild(AddDrawingFormComponent) public set content(content: AddDrawingFormComponent) {
    this.drawingFormComponent = content;
    this.changeDetectorRef.detectChanges();
  }

  private saveButtonTouched = false;
  private readonly addNewDrawingTranslationKey: string = 'GENERAL.ACTIONS.CREATED_OBJECT';
  private readonly addNewDrawingFailTranslationKey: string = 'DESIGN_LIBRARY.ERRORS.ADD_DESIGN_ERROR';
  private readonly urlToNavigateToDrawingLibrary = RouteUtils.paths.texEdit.designLibrary.absolutePath;
  private canDeleteDrawingImages = true;
  private imagesToRemove: DrawingImage[] = [];
  private drawingsToBeRecoloredCount = 0;
  private readonly formValidationHelper: FormValidationHelper = new FormValidationHelper();
  private listOfShapeAbbreviations: DrawingShapeAbbreviation[] = [];

  public constructor(
    @Inject(DRAWINGS) private drawings: Drawings,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly toastService: ToastService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly router: Router,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly navigationHelperService: NavigationHelperService<NavigationAddDrawingData>
  ) {
    super();
  }

  public ngOnInit(): void {
    this.drawingImages = this.drawings.getUploadQueue();
    this.duplicateDrawingNames = this.getDuplicateDrawingNames();

    if (isEmpty(this.drawingImages)) {
      this.navigateBack();
    } else {
      const emptyAddDrawingState = {drawingImages: null, drawingsToBeSaved: null, currentDrawingFormIndex: null} as NavigationAddDrawingData;
      const addDrawingState = this.navigationHelperService.getPartialState<NavigationAddDrawingData>(Object.keys(emptyAddDrawingState));

      if (!AssertionUtils.isNullOrUndefined(addDrawingState) && !ObjectUtils.isDeepEqual(addDrawingState, emptyAddDrawingState)) {
        this.duplicateDecided = true;
        this.drawingImages = addDrawingState.drawingImages;
        this.drawingsToBeSaved = addDrawingState.drawingsToBeSaved;
        this.currentDrawingFormIndex = addDrawingState.currentDrawingFormIndex;
        this.changeDetectorRef.detectChanges();
      } else {
        this.areImagesUploading = true;
        this.changeDetectorRef.detectChanges();

        this.drawings
          .uploadDrawingImages(this.drawingImages)
          .pipe(takeUntil(this.unSubscribeOnViewDestroy))
          .subscribe({
            next: (drawingImages: DrawingImage[]) => {
              this.drawingImages = drawingImages;
              this.areImagesUploading = false;
              this.drawingFormComponent?.initializeDrawingFormValuesFromDrawingImage();
              this.changeDetectorRef.detectChanges();
            },
            error: () => {
              this.showErrorDialogForBackendError();
            }
          });
      }

      forkJoin([this.drawings.getDrawingShapes(), this.drawings.getListOfCustomSettings()])
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe({
          next: ([drawingShapes, customSettings]: [string[], PropertyValue[]]) => {
            this.drawingShapes = drawingShapes;
            this.defaultDrawingShape = find(customSettings, {propertyName: 'shape'}).propertyValue;
            this.listOfShapeAbbreviations = customSettings.find((customSetting: PropertyValue) => customSetting.propertyName === 'shapeAbbreviations').propertyValue;
          },
          error: () => {
            this.showErrorDialogForBackendError();
          }
        });
    }
  }

  public onNavigationHelperDestroy(): void {
    this.addCurrentDrawingToDrawingsToBeSaved();
    this.navigationHelperService.savePartialState<NavigationAddDrawingData>({
      drawingImages: this.drawingImages,
      drawingsToBeSaved: this.drawingsToBeSaved,
      currentDrawingFormIndex: this.currentDrawingFormIndex
    });
  }

  public cancelAddingDrawing(): void {
    this.skipDuplication = true;
    const drawingImagesIds: string[] = filter(
      map(this.drawingImages, (drawingImage: DrawingImage) => drawingImage.id),
      (id: string) => !isNil(id)
    );
    if (size(drawingImagesIds) > 0 && this.canDeleteDrawingImages) {
      this.drawings
        .deleteDrawingImages(drawingImagesIds)
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe(() => {
          this.navigateBack();
        });
    } else {
      this.navigateBack();
    }
  }

  public getAmountOfDrawingImages(): number {
    return size(this.drawingImages);
  }

  public isBatchCreation(): boolean {
    return this.getAmountOfDrawingImages() > 1;
  }

  public canShowBatchButtons(): boolean {
    return this.isBatchCreation() && this.canShowDrawingFormSection();
  }

  public isDrawingValid(): boolean {
    return !isNil(this.drawingFormComponent) && this.drawingFormComponent.drawingForm.valid;
  }

  public canShowDrawingForm(drawingFormIndex: number): boolean {
    return isEqual(this.currentDrawingFormIndex, drawingFormIndex);
  }

  public canShowUseForButton(): boolean {
    return isEqual(this.currentDrawingFormIndex, 0) && this.drawingImagesAreAllOfTypeBmp();
  }

  public canShowBackButton(): boolean {
    return !isEqual(this.currentDrawingFormIndex, 0);
  }

  public canShowNextButton(): boolean {
    return !isEqual(this.currentDrawingFormIndex, size(this.drawingImages) - 1);
  }

  public canShowUploadButton(): boolean {
    return (!this.canShowUseForButton() || !this.isBatchCreation()) && this.canShowDrawingFormSection();
  }

  public canSaveDrawing(): boolean {
    return this.isDrawingValid() && !this.areImagesUploading;
  }

  public isCancelButtonDisabled(): boolean {
    return !this.canDeleteDrawingImages;
  }

  public nextDrawing(): void {
    this.addCurrentDrawingToDrawingsToBeSaved();
    this.currentDrawingFormIndex++;
    this.changeDetectorRef.detectChanges();
  }

  public previousDrawing(): void {
    this.addCurrentDrawingToDrawingsToBeSaved();
    this.currentDrawingFormIndex--;
    this.changeDetectorRef.detectChanges();
  }

  public uploadDrawings(): void {
    this.saveButtonTouched = true;
    this.formValidationHelper.checkForm(this.drawingFormComponent.drawingForm, this.document);
    if (this.canSaveDrawing()) {
      this.addCurrentDrawingToDrawingsToBeSaved();

      const drawings: Drawing[] = map(slice(this.drawingImages, size(this.drawingsToBeSaved)), (drawingImage: DrawingImage) => {
        return this.createDrawingFromDrawingImage(drawingImage);
      });

      this.drawingsToBeSaved.push(...drawings);

      this.save();
    }
  }

  public useForAllDrawings(): void {
    this.saveButtonTouched = true;
    this.formValidationHelper.checkForm(this.drawingFormComponent.drawingForm, this.document);

    if (this.canSaveDrawing()) {
      this.saving = true;
      const drawing: BmpDrawing = this.createDrawingFromForm() as BmpDrawing;
      this.addCurrentDrawingToDrawingsToBeSaved(drawing);

      const sliceStartIndex = 1;

      this.drawingImages.slice(sliceStartIndex).forEach((drawingImage: DrawingImage, index: number) => {
        const drawingToBeSaved: BmpDrawing = this.drawingsToBeSaved[index + sliceStartIndex] as BmpDrawing;
        this.addDrawingToDrawingsToBeSaved(drawingImage, drawingToBeSaved, drawing);
      });

      const configurations = this.drawingFormComponent.drawingForm.controls.configurations;
      const completeConfigurations = configurations.value.filter((configuration: any) => !!configuration.colorSet);

      if (AssertionUtils.isEmpty(configurations.value) || AssertionUtils.isEmpty(completeConfigurations)) {
        this.save();
      } else {
        this.drawingImages.forEach((drawingImage: DrawingImage) => {
          completeConfigurations.forEach((configuration: any) => {
            this.drawingsToBeRecoloredCount++;
            this.getRecoloredImage(configuration.colorSet, configuration.quality, drawingImage);
          });
        });
      }
    }
  }

  public getDrawingFromDrawingsToBeSaved(): Drawing {
    return this.drawingsToBeSaved[this.currentDrawingFormIndex];
  }

  public getLabelForUploadButton(): string {
    return this.isBatchCreation() ? 'GENERAL.ACTIONS.UPLOAD_ALL' : 'GENERAL.ACTIONS.UPLOAD';
  }

  public getBatchUploadText(): string | undefined {
    if (this.isBatchCreation()) {
      const batchUploadText = this.translate.instant('DESIGN_LIBRARY.DETAILS.BATCH_PROGRESS', {
        current: this.currentDrawingFormIndex + 1,
        amount: this.getAmountOfDrawingImages()
      });
      return '(' + batchUploadText + ')';
    }
  }

  public getCustomErrorMessage(): string | undefined {
    if (this.canShowImageUploadError()) {
      return this.translate.instant('DESIGN_LIBRARY.DETAILS.WAIT_FOR_UPLOAD');
    }
    if (this.canShowOverwriteWarning()) {
      return this.translate.instant('DESIGN_LIBRARY.DESIGN_WILL_BE_OVERWRITTEN');
    }
  }

  public canShowImageUploadError(): boolean {
    return this.areImagesUploading && this.saveButtonTouched;
  }

  public canShowInvalidFormMessageError(): boolean {
    const isFormInvalid = !this.canSaveDrawing();

    if (!isFormInvalid) {
      this.saveButtonTouched = false;
    }

    return isFormInvalid && this.saveButtonTouched && !this.areImagesUploading;
  }

  public canDisableNextButton(): boolean {
    return !this.isDrawingValid() || this.saving;
  }

  public canShowDuplicateDrawingNames(): boolean {
    return !isEmpty(this.duplicateDrawingNames) && !this.duplicateDecided;
  }

  public canShowDrawingFormSection(): boolean {
    return isEmpty(this.duplicateDrawingNames) || this.duplicateDecided;
  }

  public onDuplicateDecideForEachDrawing(): void {
    this.overwriteDuplicateNames = false;
    this.duplicateDecided = true;
  }

  public onDuplicateSkip(): void {
    this.skipDuplication = true;
    if (!isEmpty(this.drawings.removeDrawingImagesFromUploadQueue(this.duplicateDrawingNames))) {
      this.ngOnInit();
    } else {
      this.router.navigateByUrl(RouteUtils.paths.texEdit.designLibrary.absolutePath);
    }
  }

  public onDuplicateOverwrite(): void {
    this.overwriteDuplicateNames = true;
    this.duplicateDecided = true;
  }

  public canShowOverwriteWarning(): boolean {
    return this.drawingFormComponent?.currentDrawingImageWillBeOverwritten ?? false;
  }

  private navigateBack(): void {
    this.navigationHelperService.navigateToPreviousRoute(this.urlToNavigateToDrawingLibrary);
  }

  private createDrawingFromForm(): Drawing {
    const drawingForm: DrawingForm = this.drawingFormComponent.drawingForm;
    let result: Drawing;

    if (this.drawingImageIsOfTypeBmp(this.drawingImages[this.currentDrawingFormIndex])) {
      const configurations: DrawingConfigurationWithColorAndYarnSet[] = drawingForm.controls.configurations.value.map((configuration: any) => {
        return new DrawingConfigurationWithColorAndYarnSet(
          configuration.image,
          configuration.mainColorsCreelPosition,
          configuration.borderColorsCreelPosition,
          configuration.colorSet,
          configuration.quality,
          {
            heightInMM: convertToCommercialUnitMillimeter({
              unit: Unit.CENTIMETER,
              value: configuration.heightInCm
            }),
            widthInMM: convertToCommercialUnitMillimeter({
              unit: Unit.CENTIMETER,
              value: configuration.widthInCm
            })
          },
          configuration.yarnSet
        );
      });

      result = new BmpDrawing(
        null,
        drawingForm.value.name,
        this.drawingFormComponent.drawingImage,
        {
          heightInPx: drawingForm.getRawValue().heightInPx,
          widthInPx: drawingForm.getRawValue().widthInPx
        },
        null,
        drawingForm.value.shape,
        false,
        this.getRepeatedValue(),
        [],
        drawingForm.value.code,
        configurations,
        drawingForm.getRawValue().threadByThread,
        drawingForm.getRawValue().nrColumnsForWeftSelection
      );
    } else if (this.drawingImageIsOfTypeEp(this.drawingImages[this.currentDrawingFormIndex])) {
      result = new EpDrawing(null, drawingForm.value.name, this.drawingFormComponent.drawingImage, null, null, drawingForm.value.shape, false, this.getRepeatedValue(), null);
    } else {
      result = new TftDrawing(null, drawingForm.value.name, this.drawingFormComponent.drawingImage, drawingForm.value.shape, this.getRepeatedValue());
    }

    return result;
  }

  private getRepeatedValue(shape: string = this.drawingFormComponent.drawingForm.value.shape): boolean {
    return this.listOfShapeAbbreviations.find((shapeAbbreviation: DrawingShapeAbbreviation) => shapeAbbreviation.shape === shape).repeated;
  }

  private addCurrentDrawingToDrawingsToBeSaved(currentDrawing?: Drawing): void {
    currentDrawing = AssertionUtils.isNullOrUndefined(currentDrawing) && !this.skipDuplication ? this.createDrawingFromForm() : currentDrawing;
    if (!AssertionUtils.isNullOrUndefined(currentDrawing)) {
      if (isNil(this.drawingsToBeSaved[this.currentDrawingFormIndex])) {
        this.drawingsToBeSaved.push(currentDrawing);
      } else {
        this.drawingsToBeSaved[this.currentDrawingFormIndex] = currentDrawing;
      }
    }
  }

  private save(): void {
    this.canDeleteDrawingImages = false;
    this.saving = true;
    each(this.imagesToRemove, (imageToRemove: DrawingImage) => {
      remove(this.drawingsToBeSaved, (drawingToBeRemoved: Drawing) => isEqual(drawingToBeRemoved.name, imageToRemove.name));
    });

    this.drawings
      .saveAll(this.drawingsToBeSaved, this.overwriteDuplicateNames)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe({
        next: (ids: number[]) => {
          if (this.navigationHelperService.hasPreviousRoute()) {
            this.navigationHelperService.savePartialState<NavigationNewItemData>({newItemId: ids[0]});
          }
          this.navigateBack();

          this.toastService.showInfo({
            tapToDismiss: false,
            timeOut: 5000,
            message: this.translate.instant(this.addNewDrawingTranslationKey, {
              object: this.translate.instant('DESIGN_LIBRARY.DESIGN', {count: size(this.drawingsToBeSaved)}),
              name: '',
              count: size(this.drawingsToBeSaved)
            })
          });

          if (!isEmpty(this.imagesToRemove)) {
            let errorMessage = first(this.imagesToRemove).name;

            each(this.imagesToRemove, (imageToRemove: DrawingImage) => {
              if (!isEqual(imageToRemove, first(this.imagesToRemove))) {
                if (isEqual(imageToRemove, last(this.imagesToRemove))) {
                  errorMessage = `${errorMessage} and ${imageToRemove.name}`;
                } else {
                  errorMessage = `${errorMessage}, ${imageToRemove.name}`;
                }
              }
            });

            this.toastService.showError({
              tapToDismiss: false,
              timeOut: 20000,
              message: this.translate.instant(this.addNewDrawingFailTranslationKey, {
                name: errorMessage
              })
            });
          }
        },
        error: (error: BackendError) => {
          this.saving = false;
          this.canDeleteDrawingImages = true;
          if (!isNil(error.errorContext)) {
            this.navigateToSpecificDrawing(error.errorContext.entityName);
          }
        }
      });
  }

  private drawingImagesAreAllOfTypeBmp(): boolean {
    return isNil(find(this.drawingImages, (drawingImage: DrawingImage) => isEqual(drawingImage.type, DrawingType.EP) || isEqual(drawingImage.type, DrawingType.TFT)));
  }

  private drawingImageIsOfTypeBmp(drawingImage: DrawingImage): boolean {
    return isEqual(drawingImage.type, DrawingType.BMP);
  }

  private drawingImageIsOfTypeEp(drawingImage: DrawingImage): boolean {
    return isEqual(drawingImage.type, DrawingType.EP);
  }

  private navigateToSpecificDrawing(drawingName: string): void {
    this.addCurrentDrawingToDrawingsToBeSaved();
    this.currentDrawingFormIndex = findLastIndex(this.drawingsToBeSaved, {name: drawingName});

    this.changeDetectorRef.detectChanges();
  }

  private createDrawingFromDrawingImage(drawingImage: DrawingImage, baseDrawing?: BmpDrawing): Drawing {
    let drawing: Drawing;
    const repeatedForDefaultDrawingShape = this.getRepeatedValue(this.defaultDrawingShape);

    if (this.drawingImageIsOfTypeBmp(drawingImage)) {
      const useShapeAndConfigurationsFromBaseDrawing = !isNil(baseDrawing);

      drawing = new BmpDrawing(
        null,
        drawingImage.name,
        {
          id: drawingImage.id,
          data: drawingImage.data
        },
        drawingImage.dimensionsInPx,
        null,
        useShapeAndConfigurationsFromBaseDrawing ? baseDrawing.shape : this.defaultDrawingShape,
        false,
        repeatedForDefaultDrawingShape,
        drawingImage.uploadDrawingInfo?.designMap,
        null,
        useShapeAndConfigurationsFromBaseDrawing ? baseDrawing.configurations : [],
        useShapeAndConfigurationsFromBaseDrawing ? baseDrawing.threadByThread : drawingImage.uploadDrawingInfo?.threadByThread,
        drawingImage.uploadDrawingInfo?.nrColumnsForWeftSelection
      );
    } else if (this.drawingImageIsOfTypeEp(drawingImage)) {
      drawing = new EpDrawing(
        null,
        drawingImage.name,
        {
          id: drawingImage.id,
          data: drawingImage.data
        },
        null,
        null,
        this.defaultDrawingShape,
        false,
        repeatedForDefaultDrawingShape,
        null
      );
    } else {
      drawing = new TftDrawing(
        null,
        drawingImage.name,
        {
          id: drawingImage.id,
          data: drawingImage.data
        },
        this.defaultDrawingShape,
        repeatedForDefaultDrawingShape
      );
    }

    return drawing;
  }

  private getRecoloredImage(colorSet: OverviewListColorSet, quality: WeaveQuality, drawingImage: DrawingImage): void {
    this.drawings
      .getRecoloredImageConfiguration(
        drawingImage.id,
        quality.id,
        colorSet.id,
        null,
        null,
        TargetForListOfDrawingsEnum.DRAWING_LIBRARY,
        {input: drawingImage.dimensionsInPx},
        null,
        null,
        null,
        drawingImage.uploadDrawingInfo?.nrColumnsForWeftSelection,
        drawingImage.uploadDrawingInfo?.threadByThread
      )
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe({
        next: () => {
          this.saveAfterRecolorImage();
        },
        error: () => {
          if (!some(this.imagesToRemove, (imageToRemove: DrawingImage) => isEqual(imageToRemove.name, drawingImage.name))) {
            this.imagesToRemove.push(drawingImage);
          }

          this.saveAfterRecolorImage();
        }
      });
  }

  private saveAfterRecolorImage(): void {
    this.drawingsToBeRecoloredCount--;
    if (this.drawingsToBeRecoloredCount === 0) {
      this.save();
    }
  }

  private addDrawingToDrawingsToBeSaved(drawingImage: DrawingImage, drawingToBeSaved: BmpDrawing, drawing: BmpDrawing): void {
    if (!isNil(drawingToBeSaved)) {
      drawingToBeSaved.shape = drawing.shape;
      drawingToBeSaved.configurations = drawing.configurations;
    } else {
      drawingToBeSaved = this.createDrawingFromDrawingImage(drawingImage, drawing) as BmpDrawing;
      this.drawingsToBeSaved.push(drawingToBeSaved);
    }
  }

  private getDuplicateDrawingNames(): string[] {
    const duplicateNames: string[] = [];
    map(this.drawingImages, (drawing: Drawing) => {
      this.drawings
        .isIdentifierAvailable(drawing.name)
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe({
          next: (available: boolean) => {
            if (!available) {
              duplicateNames.push(drawing.name);
            }
          },
          error: () => {
            this.showErrorDialogForBackendError();
          }
        });
    });
    return duplicateNames;
  }

  private showErrorDialogForBackendError(): void {
    const title = this.translate.instant('GENERAL.ERRORS.BACKEND.UNAVAILABLE');
    const message = this.translate.instant('DESIGN_LIBRARY.ERRORS.CANNOT_BE_UPLOADED');

    this.dialogBuilderFactoryService
      .getBuilder()
      .openAlertDialog({titleText: title, messageText: message, type: DialogType.ERROR} as DialogOptions)
      .subscribe(() => {
        this.router.navigateByUrl(RouteUtils.paths.texEdit.designLibrary.absolutePath);
      });
  }
}
